/**
 * 复制文本到剪贴板的工具函数
 * 支持桌面和移动设备
 */

import { toast } from "react-toastify";

/**
 * 复制文本到剪贴板
 * @param {string} text - 要复制的文本
 * @param {string} successMessage - 复制成功的提示信息
 * @param {string} errorMessage - 复制失败的提示信息
 */
export function copyToClipboard(text, successMessage = '已复制到剪贴板！', errorMessage = '复制失败') {
    // 在移动设备上优先使用 Clipboard API
    if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text)
            .then(() => toast.success(successMessage))
            .catch(err => {
                // 如果 Clipboard API 失败，则尝试其他方法
                fallbackCopyTextToClipboard(text, successMessage, errorMessage);
            });
    } else {
        // 在不支持 Clipboard API 的环境中使用备选方案
        fallbackCopyTextToClipboard(text, successMessage, errorMessage);
    }
}

/**
 * 复制文本到剪贴板的备选方案
 * @param {string} text - 要复制的文本
 * @param {string} successMessage - 复制成功的提示信息
 * @param {string} errorMessage - 复制失败的提示信息
 */
function fallbackCopyTextToClipboard(text, successMessage, errorMessage) {
    // 检查是否在移动设备上
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
        // 移动设备特殊处理
        try {
            // 创建一个临时的 textarea 元素
            const textarea = document.createElement('textarea');
            textarea.value = text;
            
            // 设置样式使元素不可见
            textarea.style.position = 'fixed';
            textarea.style.left = '-9999px';
            textarea.style.top = '-9999px';
            textarea.style.opacity = '0';
            textarea.style.pointerEvents = 'none';
            
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            
            // 尝试执行复制命令
            const successful = document.execCommand('copy');
            document.body.removeChild(textarea);
            
            if (successful) {
                toast.success(successMessage);
            } else {
                // 如果 execCommand 失败，尝试使用现代方法
                copyToClipboardModern(text, successMessage, errorMessage);
            }
        } catch (err) {
            // 如果 execCommand 抛出异常，尝试使用现代方法
            copyToClipboardModern(text, successMessage, errorMessage);
        }
    } else {
        // 桌面设备使用原有逻辑
        const el = document.createElement('textarea');
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        toast.success(successMessage);
    }
}

/**
 * 使用现代 Clipboard API 的复制方法
 * @param {string} text - 要复制的文本
 * @param {string} successMessage - 复制成功的提示信息
 * @param {string} errorMessage - 复制失败的提示信息
 */
function copyToClipboardModern(text, successMessage, errorMessage) {
    // 现代方法：使用 Clipboard API（即使不在安全上下文中也尝试）
    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
            .then(() => toast.success(successMessage))
            .catch(() => {
                // 最后的备选方案：提示用户手动复制
                showMobileCopyFallback(text, successMessage, errorMessage);
            });
    } else {
        // 最后的备选方案：提示用户手动复制
        showMobileCopyFallback(text, successMessage, errorMessage);
    }
}

/**
 * 在移动设备上提示用户手动复制
 * @param {string} text - 要复制的文本
 * @param {string} successMessage - 复制成功的提示信息
 * @param {string} errorMessage - 复制失败的提示信息
 */
function showMobileCopyFallback(text, successMessage, errorMessage) {
    // 在移动设备上提示用户手动复制
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '0';
    textarea.style.top = '0';
    textarea.style.opacity = '0';
    textarea.style.width = '1px';
    textarea.style.height = '1px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    
    try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (successful) {
            toast.success(successMessage);
        } else {
            throw new Error('无法复制文本');
        }
    } catch (err) {
        document.body.removeChild(textarea);
        // 如果所有自动方法都失败了，提示用户手动复制
        const promptResult = prompt("请手动复制以下内容:", text);
        if (promptResult !== null) {
            toast.success(successMessage);
        } else {
            toast.error(errorMessage);
        }
    }
}